import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Card, Col, Descriptions, Flex, InputNumber, Result, Row, Skeleton, Space, Table, Tag, Typography } from "antd";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { ApiError, api, unwrap, type Schemas } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useTabTitle } from "@/components/tabs/WorkTabs";
import { useI18n } from "@/i18n";
import { SkuSelect } from "../catalog/mapping";
import { PoStatusTag, rupiahInput, type PurchaseOrderRow } from "./common";
import { SupplierForm } from "./Suppliers";

type Detail = Schemas["SupplierDetail"];
type Price = Detail["prices"][number];
type PriceRow = { skuId: string; skuCode: string; skuName: string; contractPrice: number | null; lastPrice: number | null };

/** A supplier: details, prices per SKU, its purchase orders. */
export function SupplierDetail() {
  const { supplierID = "" } = useParams();
  const { t, formatMoney, formatDate } = useI18n();
  const { can } = useAuth();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  // Unsaved price edits over the server's list; null shows the server's.
  const [edits, setEdits] = useState<PriceRow[] | null>(null);
  const [adding, setAdding] = useState<string | undefined>();
  const manage = can("procurement.manage");

  const detail = useQuery({
    queryKey: ["suppliers", "detail", supplierID],
    queryFn: () => unwrap(api.GET("/v1/suppliers/{supplierID}", { params: { path: { supplierID } } })),
    retry: (n, err) => !(err instanceof ApiError && err.status === 404) && n < 2,
  });
  const orders = useQuery({
    queryKey: ["purchase-orders", { supplierId: supplierID }],
    queryFn: () => unwrap(api.GET("/v1/purchase-orders", { params: { query: { supplierId: supplierID, limit: 50 } } })),
  });
  useTabTitle(detail.data?.name);
  const s = detail.data;
  const serverRows = useMemo(
    () => (s?.prices ?? []).map((p: Price) => ({ ...p, contractPrice: p.contractPrice ?? null, lastPrice: p.lastPrice ?? null })),
    [s],
  );
  const rows = edits ?? serverRows;
  const setRows = (fn: (x: PriceRow[]) => PriceRow[]) => setEdits(fn(rows));

  const savePrices = useMutation({
    mutationFn: (changed: { skuId: string; contractPrice: number | null }[]) =>
      unwrap(api.PUT("/v1/suppliers/{supplierID}/prices", { params: { path: { supplierID } }, body: { prices: changed } })),
    onSuccess: () => {
      void message.success(t("supplier.pricesSaved"));
      setEdits(null);
      void queryClient.invalidateQueries({ queryKey: ["suppliers"] });
    },
    onError: (err) => void message.error(errorText(err)),
  });
  const setStatus = useMutation({
    mutationFn: (status: "active" | "archived") =>
      unwrap(api.PUT("/v1/suppliers/{supplierID}", { params: { path: { supplierID } }, body: { ...s!, code: undefined, status } })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["suppliers"] }),
    onError: (err) => void message.error(errorText(err)),
  });

  if (detail.isError) {
    return detail.error instanceof ApiError && detail.error.status === 404 ? <Result status="404" title={t("page.notFound.title")} /> : <Result status="error" title={errorText(detail.error)} />;
  }
  if (!s) return <Skeleton active />;

  const original = new Map(s.prices.map((p: Price) => [p.skuId, p.contractPrice ?? null]));
  const changed = rows.filter((r) => original.get(r.skuId) !== r.contractPrice).map((r) => ({ skuId: r.skuId, contractPrice: r.contractPrice }));
  const removed = s.prices.filter((p: Price) => !rows.some((r) => r.skuId === p.skuId)).map((p: Price) => ({ skuId: p.skuId, contractPrice: null }));

  const addSku = async (skuId: string) => {
    setAdding(undefined);
    if (rows.some((r) => r.skuId === skuId)) return;
    const sku = await unwrap(api.GET("/v1/skus/{skuID}", { params: { path: { skuID: skuId } } }));
    setRows((x) => [...x, { skuId, skuCode: sku.code, skuName: sku.name, contractPrice: null, lastPrice: null }]);
  };

  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Space align="center" size={12} wrap>
          <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
            {s.name}
          </Typography.Title>
          <Typography.Text type="secondary" className="z-num">
            {s.code}
          </Typography.Text>
          {s.pkp && <Tag>PKP</Tag>}
          {s.status === "archived" && <Tag>{t("supplier.status.archived")}</Tag>}
        </Space>
        {manage && (
          <Space wrap>
            <Button onClick={() => setStatus.mutate(s.status === "active" ? "archived" : "active")} loading={setStatus.isPending}>
              {s.status === "active" ? t("supplier.archive") : t("supplier.restore")}
            </Button>
            <Button onClick={() => setEditing(true)}>{t("common.edit")}</Button>
            {s.status === "active" && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(`/app/procurement/orders/new?supplierId=${s.id}`)}>
                {t("supplier.newOrder")}
              </Button>
            )}
          </Space>
        )}
      </Flex>
      <Row gutter={[16, 16]}>
        <Col xs={24} xl={10}>
          <Card size="small">
            <Descriptions
              column={1}
              size="small"
              items={[
                { key: "contact", label: t("supplier.contact"), children: s.contactName || "—" },
                { key: "phone", label: t("supplier.phone"), children: s.phone || "—" },
                { key: "email", label: t("supplier.email"), children: s.email || "—" },
                { key: "address", label: t("supplier.address"), children: <span style={{ whiteSpace: "pre-wrap" }}>{s.address || "—"}</span> },
                { key: "npwp", label: t("supplier.npwp"), children: s.npwp || "—" },
                { key: "terms", label: t("supplier.terms"), children: s.paymentTermsDays === 0 ? t("po.cod") : t("po.paymentTermsValue", { n: s.paymentTermsDays }) },
                { key: "method", label: t("supplier.method"), children: t(`supplier.method.${s.paymentMethod}`) },
                { key: "bank", label: t("supplier.bank"), children: [s.bankName, s.bankAccountNo, s.bankAccountName].filter(Boolean).join(" · ") || "—" },
                { key: "note", label: t("supplier.note"), children: <span style={{ whiteSpace: "pre-wrap" }}>{s.note || "—"}</span> },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} xl={14}>
          <Card
            size="small"
            title={t("supplier.prices")}
            extra={
              manage && (changed.length > 0 || removed.length > 0) ? (
                <Button type="primary" size="small" loading={savePrices.isPending} onClick={() => savePrices.mutate([...changed, ...removed])}>
                  {t("common.save")}
                </Button>
              ) : null
            }
          >
            <Typography.Paragraph type="secondary" style={{ fontSize: 13 }}>
              {t("supplier.pricesHelp")}
            </Typography.Paragraph>
            <Table<PriceRow>
              size="small"
              rowKey="skuId"
              dataSource={rows}
              pagination={rows.length > 20 ? { pageSize: 20 } : false}
              columns={[
                {
                  title: t("catalog.code"),
                  render: (_, r) => (
                    <span>
                      <span className="z-num">{r.skuCode}</span>
                      <br />
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {r.skuName}
                      </Typography.Text>
                    </span>
                  ),
                },
                {
                  title: t("supplier.contractPrice"),
                  width: 190,
                  render: (_, r) =>
                    manage ? (
                      <InputNumber<number>
                        min={0}
                        max={1_000_000_000_000}
                        precision={0}
                        prefix="Rp"
                        {...rupiahInput}
                        value={r.contractPrice}
                        onChange={(v) => setRows((x) => x.map((y) => (y.skuId === r.skuId ? { ...y, contractPrice: v ?? null } : y)))}
                        style={{ width: "100%" }}
                      />
                    ) : r.contractPrice == null ? (
                      "—"
                    ) : (
                      <span className="z-num">{formatMoney(r.contractPrice)}</span>
                    ),
                },
                {
                  title: t("supplier.lastPrice"),
                  dataIndex: "lastPrice",
                  width: 140,
                  align: "right",
                  render: (v: number | null) => (v == null ? <Typography.Text type="secondary">—</Typography.Text> : <span className="z-num">{formatMoney(v)}</span>),
                },
                ...(manage
                  ? [
                      {
                        width: 48,
                        render: (_: unknown, r: PriceRow) => (
                          <Button type="text" icon={<DeleteOutlined />} aria-label={t("common.delete")} onClick={() => setRows((x) => x.filter((y) => y.skuId !== r.skuId))} />
                        ),
                      },
                    ]
                  : []),
              ]}
            />
            {manage && (
              <div style={{ marginTop: 12 }}>
                <SkuSelect value={adding} onChange={(id) => void addSku(id)} singleOnly placeholder={t("supplier.addPrice")} />
              </div>
            )}
          </Card>
        </Col>
        <Col span={24}>
          <Card size="small" title={t("supplier.orders")}>
            <Table<PurchaseOrderRow>
              size="small"
              rowKey="id"
              loading={orders.isFetching}
              dataSource={orders.data?.items}
              pagination={false}
              locale={{ emptyText: t("po.empty") }}
              onRow={(o) => ({ onClick: () => navigate(`/app/procurement/orders/${o.id}`), style: { cursor: "pointer" } })}
              columns={[
                { title: t("po.number"), dataIndex: "number", width: 140, render: (v: string) => <Typography.Text strong className="z-num">{v}</Typography.Text> },
                { title: t("common.status"), dataIndex: "status", width: 170, render: (v: PurchaseOrderRow["status"]) => <PoStatusTag status={v} /> },
                { title: t("po.warehouse"), dataIndex: "warehouseName" },
                { title: t("po.orderDate"), dataIndex: "orderDate", width: 130, render: (v: string) => formatDate(v) },
                { title: t("po.total"), dataIndex: "total", width: 150, align: "right", render: (v: number) => <span className="z-num">{formatMoney(v)}</span> },
              ]}
            />
          </Card>
        </Col>
      </Row>
      <SupplierForm open={editing} supplier={s} onClose={() => setEditing(false)} />
    </>
  );
}
